import React, { useEffect, useState } from "react";
import { useApp } from "@/context/AppContext";
import { adminListOrders, adminUpdateOrderStatus, fileUrl, whatsappUrl, buildOrderWhatsAppText } from "@/lib/api";
import { statusColor } from "@/lib/i18n";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Package, Phone, MapPin, User, ImageOff, StickyNote, Filter, MessageCircle, FileText } from "lucide-react";
import { toast } from "sonner";

const STATUSES = ["placed", "packed", "out_for_delivery", "delivered", "cancelled"];

const AdminOrdersPage = () => {
  const { t, lang, adminToken } = useApp();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState("all");
  const [selected, setSelected] = useState(null);

  const load = () => {
    setLoading(true);
    adminListOrders(adminToken, statusFilter === "all" ? null : statusFilter)
      .then(setOrders)
      .finally(() => setLoading(false));
  };

  useEffect(load, [adminToken, statusFilter]);

  const updateStatus = async (orderId, newStatus) => {
    try {
      const updated = await adminUpdateOrderStatus(adminToken, orderId, newStatus);
      setOrders((prev) => prev.map((o) => o.id === orderId ? updated : o));
      if (selected?.id === orderId) setSelected(updated);
      toast.success("Status updated");
    } catch (err) {
      toast.error("Failed to update");
    }
  };

  return (
    <div data-testid="admin-orders-page">
      <div className="flex items-center justify-between mb-6 gap-4 flex-wrap">
        <div>
          <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#1E293B]">{t("orders")}</h1>
          <p className="text-sm text-slate-500">Manage and update delivery status</p>
        </div>
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-slate-500" />
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[180px] bg-white h-10" data-testid="orders-status-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="bg-white">
              <SelectItem value="all">All</SelectItem>
              {STATUSES.map((s) => <SelectItem key={s} value={s}>{t(s)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: 4 }).map((_, i) => <div key={i} className="h-20 bg-white border border-[#E2E8F0] rounded-md animate-pulse" />)}
        </div>
      ) : orders.length === 0 ? (
        <div className="text-center py-16 bg-white border border-[#E2E8F0] rounded-md">
          <Package className="w-12 h-12 mx-auto text-slate-300 mb-2" />
          <p className="text-slate-500">{t("noOrders")}</p>
        </div>
      ) : (
        <div className="bg-white border border-[#E2E8F0] rounded-md overflow-hidden">
          <div className="hidden md:grid grid-cols-12 gap-4 px-4 py-3 border-b border-[#E2E8F0] text-xs font-semibold uppercase tracking-wider text-slate-500 bg-[#F9F7F3]">
            <div className="col-span-3">{t("orderNumber")}</div>
            <div className="col-span-3">{t("customer")}</div>
            <div className="col-span-2">{t("orderDate")}</div>
            <div className="col-span-2">{t("total")}</div>
            <div className="col-span-2">{t("orderStatus")}</div>
          </div>
          <div className="divide-y divide-[#E2E8F0]">
            {orders.map((o) => (
              <button
                key={o.id}
                onClick={() => setSelected(o)}
                data-testid={`admin-order-row-${o.id}`}
                className="w-full text-left grid grid-cols-1 md:grid-cols-12 gap-2 md:gap-4 px-4 py-4 hover:bg-[#F9F7F3] transition-colors"
              >
                <div className="md:col-span-3">
                  <p className="font-mono font-semibold text-sm text-[#1E293B]">{o.order_number}</p>
                  <p className="text-xs text-slate-500 md:hidden mt-1">{o.delivery_name}</p>
                </div>
                <div className="hidden md:block md:col-span-3">
                  <p className="text-sm font-medium text-[#1E293B]">{o.delivery_name}</p>
                  <p className="text-xs text-slate-500">{o.delivery_mobile}</p>
                </div>
                <div className="hidden md:block md:col-span-2 text-xs text-slate-600">
                  {new Date(o.created_at).toLocaleDateString(lang === "hi" ? "hi-IN" : "en-IN", { dateStyle: "medium" })}
                </div>
                <div className="md:col-span-2 flex items-center justify-between md:block">
                  <p className="font-heading font-bold text-[#2D5A27]">₹{o.total.toFixed(0)}</p>
                  <p className="text-xs text-slate-500 md:hidden">{o.items.length} {t("items")}</p>
                </div>
                <div className="md:col-span-2">
                  <Badge variant="outline" className={`${statusColor[o.status]} font-semibold`}>{t(o.status)}</Badge>
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-order-dialog">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="font-heading text-xl flex items-center gap-2">
                  {selected.order_number}
                  <Badge variant="outline" className={statusColor[selected.status]}>{t(selected.status)}</Badge>
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
                  <div className="p-3 rounded-md bg-[#F9F7F3] border border-[#E2E8F0]">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t("customer")}</p>
                    <p className="font-medium flex items-center gap-1.5"><User className="w-3.5 h-3.5" />{selected.delivery_name}</p>
                    <p className="text-slate-600 flex items-center gap-1.5 mt-1"><Phone className="w-3.5 h-3.5" />{selected.delivery_mobile}</p>
                  </div>
                  <div className="p-3 rounded-md bg-[#F9F7F3] border border-[#E2E8F0]">
                    <p className="text-xs uppercase tracking-wider text-slate-500 mb-1">{t("address")}</p>
                    <p className="text-sm flex items-start gap-1.5"><MapPin className="w-3.5 h-3.5 mt-0.5 shrink-0" />{selected.delivery_address}</p>
                  </div>
                </div>

                {selected.notes && (
                  <div className="p-3 rounded-md bg-amber-50 border border-amber-200 flex items-start gap-2 text-sm">
                    <StickyNote className="w-4 h-4 text-amber-700 mt-0.5" />
                    <span className="text-amber-900">{selected.notes}</span>
                  </div>
                )}

                <div>
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("itemsOrdered")}</p>
                  <div className="space-y-2">
                    {selected.items.map((it, i) => {
                      const img = fileUrl(it.image_path);
                      const name = lang === "hi" ? it.name_hi : it.name_en;
                      return (
                        <div key={i} className="flex items-center gap-3 p-2 rounded-md border border-[#E2E8F0]">
                          <div className="w-12 h-12 rounded-md bg-[#EFEBE2] overflow-hidden shrink-0">
                            {img ? <img src={img} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-slate-400"><ImageOff className="w-4 h-4" /></div>}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium truncate">{name}</p>
                            <p className="text-xs text-slate-500">{it.unit} × {it.quantity}</p>
                          </div>
                          <p className="font-semibold text-sm">₹{(it.price * it.quantity).toFixed(0)}</p>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="border-t border-[#E2E8F0] pt-3 space-y-1 text-sm">
                  <div className="flex justify-between"><span className="text-slate-600">{t("subtotal")}</span><span>₹{selected.subtotal.toFixed(0)}</span></div>
                  <div className="flex justify-between"><span className="text-slate-600">{t("deliveryFee")}</span><span>₹{selected.delivery_fee.toFixed(0)}</span></div>
                  <div className="flex justify-between text-base pt-1">
                    <span className="font-heading font-semibold">{t("total")}</span>
                    <span className="font-heading font-bold text-[#2D5A27]">₹{selected.total.toFixed(0)}</span>
                  </div>
                </div>

                <div className="border-t border-[#E2E8F0] pt-4">
                  <p className="text-xs uppercase tracking-wider text-slate-500 mb-2">{t("updateStatus")}</p>
                  <div className="flex flex-wrap gap-2">
                    {STATUSES.map((s) => (
                      <Button
                        key={s}
                        variant={selected.status === s ? "default" : "outline"}
                        size="sm"
                        onClick={() => updateStatus(selected.id, s)}
                        data-testid={`update-status-${s}`}
                        className={`${selected.status === s ? "bg-[#2D5A27] text-white hover:bg-[#21421D]" : ""}`}
                      >
                        {t(s)}
                      </Button>
                    ))}
                  </div>
                </div>

                <div className="border-t border-[#E2E8F0] pt-4 flex flex-wrap gap-2">
                  <a
                    href={whatsappUrl(selected.delivery_mobile.length === 10 ? `91${selected.delivery_mobile}` : selected.delivery_mobile, buildOrderWhatsAppText(selected, lang === "hi"))}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="wa-message-customer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold"
                  >
                    <MessageCircle className="w-4 h-4" /> {t("whatsappCustomer")}
                  </a>
                  <a
                    href={`tel:${selected.delivery_mobile}`}
                    data-testid="call-customer"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F9F7F3] text-sm font-semibold"
                  >
                    <Phone className="w-4 h-4" /> {selected.delivery_mobile}
                  </a>
                  <a
                    href={`/invoice/${selected.id}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="admin-invoice-link"
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-md border border-[#E2E8F0] hover:bg-[#F9F7F3] text-sm font-semibold"
                  >
                    <FileText className="w-4 h-4" /> {t("invoice")}
                  </a>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminOrdersPage;
